
import { ref } from 'vue'
import { GET_RECIPE } from '../graphql/queries'
import { showError } from './errorHandling'
import { useQuery } from '@vue/apollo-composable'
import apolloClient from '../graphql/apolloClient'

// Composable for reactive recipe fetching (to be used at component setup)
export function useRecipeNavigation() {
    const recipeIdToFetch = ref<string | null>(null)
    const recipeSlug = ref<string | null>(null)

    const { result: recipeResult, loading: loadingRecipe, error: errorRecipe } = useQuery(
        GET_RECIPE,
        () => ({ id: recipeIdToFetch.value }),
        () => ({
            enabled: !!recipeIdToFetch.value, // Only run when we have a recipe ID
            fetchPolicy: 'network-only' // Always fetch fresh data
        })
    )

    const goToRecipe = async (recipeID: string | null) => {
        try {
            recipeIdToFetch.value = recipeID

            console.log('🔍 Fetching recipe with ID:', recipeIdToFetch.value)

            await new Promise(resolve => setTimeout(resolve, 100))

            if (loadingRecipe.value) {
                while (loadingRecipe.value) {
                    await new Promise(resolve => setTimeout(resolve, 100))
                }
            }

            if (errorRecipe.value) {
                throw errorRecipe.value
            }

            if (recipeResult.value?.recipe) {
                recipeSlug.value = recipeResult.value.recipe.slug
                recipeIdToFetch.value = null
            } else {
                throw new Error('Recipe not found')
            }
        } catch (error) {
            console.error('❌ Error in goToRecipe:', error)
            showError(error, 'fetch recipe')
            recipeIdToFetch.value = null
        }
    }

    return {
        goToRecipe,
        recipeResult,
        recipeSlug,
        loadingRecipe,
        errorRecipe
    }
}

// Standalone function to fetch recipe by ID (can be called from anywhere)
export async function fetchRecipeById(recipeId: string) {
    try {
        console.log('🔍 Fetching recipe with ID:', recipeId)
        
        const result = await apolloClient.query({
            query: GET_RECIPE,
            variables: { id: recipeId },
            fetchPolicy: 'network-only'
        })

        if (result.data?.recipe) {
            console.log('✅ Recipe fetched successfully:', result.data.recipe)
            return result.data.recipe
        } else {
            throw new Error('Recipe not found')
        }
    } catch (error) {
        console.error('❌ Error fetching recipe:', error)
        showError(error, 'fetch recipe')
        throw error
    }
}

